"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Heart } from "lucide-react"
// Removed createClient import - using API endpoint instead
import { Alert, AlertDescription } from "@/components/ui/alert"
import { getCountryFlag } from "@/lib/utils/country-flags"

interface SpotImage {
  id: string
  unsplash_id: string
  unsplash_url: string
  unsplash_thumb_url: string
  photographer_name: string
  photographer_username: string
  photographer_url: string
  votes: number
  user_voted?: boolean
}

interface SpotImageVotingProps {
  spotId: string
  currentUserId?: string
  country?: string | null
}

const UTM_PARAMS = "utm_source=surf_spots&utm_medium=referral"

const DEFAULT_PLACEHOLDER = {
  id: "0XBjklLq5rg",
  unsplash_url: "",
  unsplash_thumb_url: "",
  photographer_name: "",
  photographer_username: "",
  photographer_url: "",
}

export function SpotImageVoting({ spotId, currentUserId, country }: SpotImageVotingProps) {
  const [images, setImages] = useState<SpotImage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [placeholder, setPlaceholder] = useState(DEFAULT_PLACEHOLDER)

  useEffect(() => {
    fetchPlaceholderPhoto()
  }, [])

  useEffect(() => {
    loadImages()
  }, [spotId, currentUserId])

  const fetchPlaceholderPhoto = async () => {
    try {
      const response = await fetch(`/api/unsplash/photo?id=${DEFAULT_PLACEHOLDER.id}`)
      if (response.ok) {
        const responseData = await response.json()
        
        // Handle both success response (wrapped in data) and fallback response (direct structure)
        let photoData
        if (responseData.success && responseData.data) {
          // Success response from createSecureSuccessResponse
          photoData = responseData.data
        } else if (responseData.fallback) {
          // Fallback response (rate limited or error)
          // Don't update placeholder, keep default empty values
          return
        } else {
          // Direct response structure (legacy or error)
          photoData = responseData
        }
        
        // Validate that we have the required fields
        if (photoData && photoData.urls && photoData.urls.regular && photoData.user) {
          setPlaceholder({
            id: photoData.id || DEFAULT_PLACEHOLDER.id,
            unsplash_url: photoData.urls.regular,
            unsplash_thumb_url: photoData.urls.thumb || photoData.urls.regular,
            photographer_name: photoData.user.name || "",
            photographer_username: photoData.user.username || "",
            photographer_url: photoData.user.profile_url ? `${photoData.user.profile_url}?${UTM_PARAMS}` : "",
          })
        }
      }
    } catch (err) {
      console.error("Error fetching placeholder photo:", err)
      // Keep default empty values if fetch fails
    }
  }

  const loadImages = async () => {
    try {
      console.log("Loading images for spot:", spotId)
      setLoading(true)
      
      // Use API endpoint instead of direct Supabase client to avoid hanging
      const response = await fetch(`/api/spot-images?spotId=${spotId}`)
      
      if (!response.ok) {
        console.error("Error loading images:", response.status, response.statusText)
        throw new Error(`Failed to load images: ${response.status}`)
      }

      const result = await response.json()
      
      if (!result.success) {
        console.error("API returned error:", result.error)
        throw new Error(result.error || "Failed to load images")
      }

      // API returns { success: true, images: [...], count: N } per API_REFERENCE.md
      let imagesData = result.images || []
      console.log("Images loaded:", imagesData.length)

      // If we have images, enhance them with photographer info if needed
      if (imagesData.length > 0) {
        // Check if any images have unsplash_id but generic photographer info
        const enhancedImages = await Promise.all(
          imagesData.map(async (img: SpotImage) => {
            // If image has unsplash_id but photographer is generic "Unsplash", fetch real info
            if (img.unsplash_id && img.photographer_name === "Unsplash" && img.photographer_username === "unsplash") {
              try {
                const photoResponse = await fetch(`/api/unsplash/photo?id=${img.unsplash_id}`)
                if (photoResponse.ok) {
                  const photoData = await photoResponse.json()
                  if (photoData.user && !photoData.fallback) {
                    return {
                      ...img,
                      photographer_name: photoData.user.name,
                      photographer_username: photoData.user.username,
                      photographer_url: `${photoData.user.profile_url}?${UTM_PARAMS}`,
                    }
                  }
                }
              } catch (err) {
                console.error("Error fetching photographer info:", err)
              }
            }
            return img
          })
        )
        
        setImages(enhancedImages)
        setLoading(false)
        return
      }

      // No images found
      console.log("No images found for spot")
      setImages([])
    } catch (err) {
      console.error("Error loading images:", err)
      setError("Failed to load images")
    } finally {
      setLoading(false)
    }
  }

  const handleVote = async (imageId: string, currentlyVoted: boolean) => {
    if (!currentUserId) {
      alert("Please sign in to vote on images")
      return
    }

    try {
      // Use API endpoint for voting (would need to create this endpoint)
      // For now, keep using direct Supabase client for voting since it's user-specific
      // and requires authentication - this is a smaller operation that shouldn't hang
      const supabase = (await import("@/lib/supabase/client")).createClient()
      
      if (currentlyVoted) {
        // Remove vote
        const { error: deleteError } = await supabase
          .from("spot_image_votes")
          .delete()
          .eq("image_id", imageId)
          .eq("user_id", currentUserId)

        if (deleteError) throw deleteError

        // Decrement vote count
        const { error: updateError } = await supabase.rpc("decrement_image_votes", { image_id: imageId })

        if (updateError) throw updateError
      } else {
        // Add vote
        const { error: insertError } = await supabase
          .from("spot_image_votes")
          .insert({ image_id: imageId, user_id: currentUserId })

        if (insertError) throw insertError

        // Increment vote count
        const { error: updateError } = await supabase.rpc("increment_image_votes", { image_id: imageId })

        if (updateError) throw updateError
      }

      // Reload images to reflect new vote counts
      await loadImages()
    } catch (err) {
      console.error("Error voting:", err)
      alert("Failed to register vote. Please try again.")
    }
  }

  if (loading) {
    return (
      <div className="w-full h-full bg-slate-200 animate-pulse flex items-center justify-center">
        <div className="text-slate-400">Loading image...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-full bg-slate-100 flex items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (images.length === 0) {
    return (
      <div className="relative w-full h-full">
        <img
          src={placeholder.unsplash_url || "/placeholder.svg?height=400&width=800&query=ocean wave"}
          alt="Ocean wave placeholder"
          className="w-full h-full object-cover"
        />
        {placeholder.photographer_name && (
          <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-md z-20">
            <p className="text-white/80 text-[8px] font-medium leading-none">
              Photo by{" "}
              <a
                href={placeholder.photographer_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                {placeholder.photographer_name}
              </a>
            </p>
          </div>
        )}
      </div>
    )
  }

  const topImage = images[0]

  return (
    <div className="relative w-full h-full">
      <img
        src={topImage.unsplash_url || "/placeholder.svg"}
        alt={`Photo by ${topImage.photographer_name}`}
        className="w-full h-full object-cover"
      />
      {/* Country Flag - Top Left */}
      {country && (
        <div className="absolute top-6 left-6 bg-black/60 backdrop-blur-sm rounded-full p-2.5 flex items-center justify-center z-20">
          <span className="text-2xl leading-none">{getCountryFlag(country)}</span>
        </div>
      )}
      {/* Unsplash credits badge - bottom right */}
      <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-md z-20">
        <p className="text-white/80 text-[8px] font-medium leading-none">
          Photo by{" "}
          <a
            href={topImage.photographer_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white"
          >
            {topImage.photographer_name}
          </a>
        </p>
      </div>
    </div>
  )
}
